import React, { useEffect, useRef, useState } from "react";

import { useForm } from "react-hook-form";

import DataTableDefault, { createTheme } from "react-data-table-component";
import ReactPaginate from "react-paginate";

import {
  Button,
  Form,
  Popover,
  PopoverBody,
  PopoverHeader,
  Spinner,
  Table,
} from "reactstrap";

import { ArrowRightCircle, ChevronDown } from "react-feather";

import { AlertList } from "components/Structure/AlertList";

import { useSkin } from "@hooks/useSkin";

import * as S from "./styles";

const paginationComponentOptions = {
  rowsPerPageText: "Items por página",
  rangeSeparatorText: "de",
  selectAllRowsItem: true,
  selectAllRowsItemText: "Todos",
};

createTheme("dark", {
  background: {
    default: "transparent",
  },
});

const CustomPagination = ({
  totalPages,
  handlePageChange,
  page: pageItem,
  paginationPosition,
  reset,
  inputPagination,
}) => (
  <ReactPaginate
    nextLabel=""
    breakLabel="..."
    previousLabel=""
    pageCount={totalPages}
    activeClassName="active"
    breakClassName="page-item"
    pageClassName={"page-item"}
    breakLinkClassName="page-link"
    nextLinkClassName={"page-link"}
    pageLinkClassName={"page-link"}
    nextClassName={"page-item next"}
    previousLinkClassName={"page-link"}
    previousClassName={"page-item prev"}
    onPageChange={({ selected }) => {
      if (handlePageChange) handlePageChange(selected === 0 ? 1 : selected + 1);
      if (inputPagination) {
        reset();
      }
    }}
    forcePage={pageItem !== 0 ? pageItem - 1 : 0}
    containerClassName={`pagination react-paginate ${paginationPosition} p-1 mb-0`}
  />
);

const NoData = ({ titleError, isSuccess = false }) => (
  <AlertList
    isSuccess={isSuccess}
    title="Informativo"
    description={titleError || "Ainda não há dados cadastrados nessa seção."}
  />
);

const ProgressPage = () => (
  <div className="text-center pt-4 pb-4">
    <Spinner />
    <h4>Carregando dados....</h4>
  </div>
);

const CellRow = ({ column, item }) => {
  if (column.cell) {
    return column.cell(item);
  }

  return item[column.name];
};

const PaginationComponent = ({
  paginationClass,
  paginationExtraComponent,
  paginationPosition,
  totalPages,
  handlePageChange,
  page,
  extraProps,
  inputPagination,
  control,
  handleSubmit,
  onChangePageByInput,
  newPageWatch,
  reset,
  focus,
  setFocus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const paginationInputRef = useRef(null);

  useEffect(() => {
    if (newPageWatch?.length && focus) {
      setIsOpen(!!newPageWatch?.length);
    }
  }, [newPageWatch, page, setIsOpen, focus]);

  const onPaginate = async () => {
    await handleSubmit(async data => {
      await onChangePageByInput(data);
      reset();
    })();
  };

  const parsedPage = Number(newPageWatch);
  const isValidPage =
    parsedPage &&
    parsedPage !== page &&
    parsedPage >= 1 &&
    parsedPage <= totalPages;

  const currentPage = parsedPage === page;

  const textClause = currentPage
    ? "Página atual"
    : isValidPage
    ? "Página encontrada!"
    : "Página não encontrada";

  const colorClause = currentPage
    ? "warning"
    : isValidPage
    ? "success"
    : "danger";

  const handleFocus = value => setFocus(value);

  return (
    <div
      className={`d-flex align-items-center  ${
        paginationClass || "justify-content-end mt-1 gap-1"
      }`}
    >
      {paginationExtraComponent && paginationExtraComponent}
      <div className="d-flex align-items-center justify-content-end mt-1 gap-3">
        <CustomPagination
          paginationPosition={paginationPosition}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          page={page}
          reset={reset}
          inputPagination={inputPagination}
          {...extraProps}
        />
        {inputPagination && (
          <>
            <S.DivMaxW ref={paginationInputRef} className="mb-2 me-3">
              <small>Página</small>
              <Form
                onSubmit={handleSubmit(data => {
                  if (isValidPage) {
                    onChangePageByInput(data);
                  }
                })}
              >
                <S.InputChangePage
                  name="newPage"
                  placeholder="1"
                  type="number"
                  control={control}
                  onClick={() => handleFocus(true)}
                  onFocus={() => handleFocus(true)}
                  onBlur={() => handleFocus(false)}
                  autoFocus={focus}
                />
              </Form>
            </S.DivMaxW>
            <Popover
              placement="top"
              target={paginationInputRef}
              isOpen={isOpen && newPageWatch?.length}
              toggle={() => setIsOpen(!isOpen)}
            >
              <PopoverHeader>Paginação</PopoverHeader>
              <PopoverBody className="d-flex flex-column">
                <b className={`text-${colorClause}`}>{textClause}</b>
                {isValidPage && !currentPage ? (
                  <S.PaginationBtnDiv className="text-center">
                    <Button color="success" size="sm" onClick={onPaginate}>
                      <ArrowRightCircle size={22} />
                    </Button>
                  </S.PaginationBtnDiv>
                ) : null}
              </PopoverBody>
            </Popover>
          </>
        )}
      </div>
    </div>
  );
};

// Mesma paginação do DataTable (com "ir para página"), para telas que
// renderizam a tabela com pagination={false} e paginam por fora.
export const DataTablePagination = ({
  totalPages,
  page = 1,
  onPageChange = () => {},
  inputPagination = true,
  paginationClass,
  paginationPosition = `justify-content-end`,
}) => {
  const { handleSubmit, control, watch, reset } = useForm();

  const [focus, setFocus] = useState(false);

  const onChangePageByInput = data => {
    const newPage = Number(data?.newPage);
    if (!newPage || newPage > totalPages || newPage < 1) return;
    setFocus(false);

    onPageChange(newPage);
  };

  return (
    <PaginationComponent
      paginationClass={paginationClass}
      paginationPosition={paginationPosition}
      totalPages={totalPages}
      handlePageChange={onPageChange}
      page={page}
      inputPagination={inputPagination}
      control={control}
      handleSubmit={handleSubmit}
      newPageWatch={watch("newPage")}
      onChangePageByInput={onChangePageByInput}
      reset={reset}
      focus={focus}
      setFocus={setFocus}
    />
  );
};

export const DataTable = ({
  isLoading,
  totalDocs,
  totalPages,
  page = 1,
  data = [],
  pagination = true,
  isError = false,
  paginationServer = true,
  handlePageChange = () => {},
  handlePerRowsChange = () => {},
  handleInputPagination = () => {},
  onSort = () => {},
  titleError,
  isSuccessNotData = false,
  paginationPosition = `justify-content-end`,
  type = "full",
  inputPagination = false,
  paginationClass,
  paginationExtraComponent,
  ...props
}) => {
  const { handleSubmit, control, watch, reset } = useForm();

  const [focus, setFocus] = useState(false);

  const { skin } = useSkin();

  const onChangePageByInput = data => {
    if (data?.newPage > totalPages || data?.newPage < 1) return;
    setFocus(false);

    handleInputPagination(data?.newPage);
  };

  const newPageWatch = watch("newPage");

  if (type === "simple") {
    const columns = props.columns ?? [];
    return (
      <Table responsive bordered>
        <thead>
          <tr>
            {columns?.map((item, key) => (
              <th
                className={item.className}
                key={`column-simple-${key}-${item.name}`}
              >
                {item.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={columns.length}>
                <ProgressPage />
              </td>
            </tr>
          )}
          {!isLoading &&
            data?.length > 0 &&
            data?.map(dataItem => (
              <tr key={dataItem.id}>
                {columns.map((column, key) => (
                  <td key={`column-td-simple-${key}-${column.name}`}>
                    <div className={column.className}>
                      <CellRow column={column} item={dataItem} />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </Table>
    );
  }

  return (
    <DataTableDefault
      pagination={pagination}
      paginationServer={paginationServer}
      data={data}
      onSort={onSort}
      theme={skin === "dark" ? "dark" : "default"}
      sortIcon={onSort && <ChevronDown />}
      paginationDefaultPage={page}
      progressPending={isLoading}
      paginationComponentOptions={paginationComponentOptions}
      paginationTotalRows={totalDocs}
      onChangeRowsPerPage={handlePerRowsChange}
      paginationComponent={extraProps =>
        pagination && (
          <PaginationComponent
            paginationClass={paginationClass}
            paginationExtraComponent={paginationExtraComponent}
            paginationPosition={paginationPosition}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            page={page}
            extraProps={extraProps}
            inputPagination={inputPagination}
            control={control}
            handleSubmit={handleSubmit}
            newPageWatch={newPageWatch}
            onChangePageByInput={onChangePageByInput}
            reset={reset}
            focus={focus}
            setFocus={setFocus}
          />
        )
      }
      noDataComponent={
        isError ? (
          <div>{titleError ? titleError : "Nenhum dados para listar"}</div>
        ) : (
          !isLoading &&
          !data.length && (
            <NoData isSuccess={isSuccessNotData} titleError={titleError} />
          )
        )
      }
      progressComponent={<ProgressPage />}
      {...props}
    />
  );
};
